import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { ItemSourceMeanValidatorModel } from 'models/ItemSourceMeanValidatorModel';
import { IItemSourceMeanValidatorCreate } from 'types/itemSourceMeanValidator';
import DialectSelect, { IDialectOption } from 'components/selects/DialectSelect';
import KLineSelectorList from 'components/KLineSelectorList';

interface IProps {
  model: ItemSourceMeanValidatorModel;
  onClose: () => void;
  onSuccess: (jobId: number) => void;
}

const ItemSourceMeanValidatorCreate: React.FC<IProps> = ({ model, onClose, onSuccess }) => {
  const [name, setName] = useState<string>('');
  const [dialectId, setDialectId] = useState<number | ''>('');
  const [includePreviousValidated, setIncludePreviousValidated] = useState<boolean>(false);
  const [klineIds, setKlineIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDialectChange = useCallback((dialect: IDialectOption) => {
    setDialectId(dialect.dialectId);
  }, []);

  const handleCreate = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Please enter a name');
      return;
    }
    if (!dialectId) {
      setError('Please select a dialect');
      return;
    }
    setSaving(true);
    try {
      const payload: IItemSourceMeanValidatorCreate = {
        name: name.trim(),
        dialect_id: Number(dialectId),
        include_previous_validated: includePreviousValidated,
        filter_kline_ids: klineIds.length ? klineIds : undefined,
      };
      const res = await model.createJob(payload);
      if (res.data?.id) {
        onSuccess(res.data.id);
      }
    } catch (e) {
      setError('Failed to create job');
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Create Item Source Mean Validator Job</Title>
        <CloseButton onClick={onClose}>×</CloseButton>
      </Header>
      <Content>
        <Section>
          <SectionTitle>Name</SectionTitle>
          <NameInput
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter a descriptive name for this job"
            autoFocus
          />
        </Section>
        <Section>
          <SectionTitle>Dialect</SectionTitle>
          <DialectSelect handleDialectChange={handleDialectChange} onlySource />
        </Section>
        <Section>
          <CheckboxContainer>
            <Checkbox
              type="checkbox"
              id="includePreviousValidated"
              checked={includePreviousValidated}
              onChange={(e) => setIncludePreviousValidated(e.target.checked)}
            />
            <CheckboxLabel htmlFor="includePreviousValidated">
              Include previously validated meanings
            </CheckboxLabel>
          </CheckboxContainer>
        </Section>
        <Section>
          <SectionTitle>KLines (optional)</SectionTitle>
          <KLineSelectorList value={klineIds} onChange={setKlineIds} />
        </Section>
        {error && <ErrorText>{error}</ErrorText>}
      </Content>
      <Footer>
        <CancelButton onClick={onClose}>Cancel</CancelButton>
        <CreateButton onClick={handleCreate} disabled={saving}>
          {saving ? 'Creating...' : 'Create'}
        </CreateButton>
      </Footer>
    </Container>
  );
};

export default ItemSourceMeanValidatorCreate;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: white;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 30px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  margin-bottom: 10px;
  font-size: 16px;
  color: #333;
`;

const ErrorText = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 5px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const CreateButton = styled.button`
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const NameInput = styled.input`
  width: 94%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  cursor: pointer;
  font-size: 14px;
  color: #333;
`;